import type { Node, NodeOrderCode } from "interfaces/schemas";
import * as treeUtils from "utils/treeUtils";
import { createNodesInput } from "./utils/createNodeInput";
import { NodeKinds } from "interfaces/enums/nodeKinds";
import { processNodes } from "./utils/mutations";
import type { Project } from "../projectStore";
import type { ProductOutput } from "stores/product";
import { getInputFromOutput } from "stores/product/product/utils/getInputFromOutput";
import { getAccessoryNameFromGroup } from "stores/product/product/utils";

export const updateNodeConfiguration = (
  nodeId: string,
  productOutput: ProductOutput,
  tree: Node[],
  project: Project,
  language: string,
  callback?: (node: Node) => void,
) => {
  if (!productOutput) {
    throw new Error("Product output is required to update node configuration");
  }
  const config = productOutput.product;
  // node
  const nodeData = treeUtils.findNodeById({
    nodes: tree,
    id: nodeId,
  });

  if (!nodeData) {
    throw new Error("Node not found");
  }

  if (
    !treeUtils.isProduct(nodeData) &&
    !treeUtils.isProductAccessory(nodeData)
  ) {
    throw new Error("Node is not a product");
  }

  const seriesId = nodeData.orderCode?.seriesId;
  if (!seriesId) {
    throw new Error("Product node is missing series ID in order code");
  }

  const presentation = config?.orderCode?.presentation;
  if (!presentation) {
    throw new Error("Presentation is required to update node configuration");
  }

  const hasAccessories = (config?.selectedAccessories?.length ?? 0) > 0;

  const productInput = getInputFromOutput(productOutput, seriesId);

  const orderCode: NodeOrderCode = {
    seriesId: productInput.seriesId,
    state: {
      optionStates: productInput?.state?.orderCodeState?.optionStates,
    },
    currentStrategyId: productInput?.state?.currentStrategyId,
    propertyStates: productInput?.state?.propertyStates ?? undefined,
  };

  const pimId = productOutput.productImageList?.productImages?.find(
    (image) => image?.typ === "ProductPhoto",
  )?.pimId;

  const newNodeState = createNodesInput({
    data: {
      ...nodeData,
      orderCode,
      pimId: pimId ?? null,
      presentation,
      nodes: hasAccessories
        ? (config?.selectedAccessories?.map((accessory, index) => {
            return {
              accessoryGroupId: accessory?.accessoryGroupId,
              accessoryId: accessory?.accessoryId,
              id: "dummy-accessory-id",
              parentId: nodeData.id,
              kind: NodeKinds.PRODUCT_ACCESSORY,
              position: index,
              active: nodeData.active,
              productName: getAccessoryNameFromGroup(
                productOutput,
                accessory?.currentPresentation,
                accessory?.accessoryGroupId,
              ),
              presentation: accessory?.currentPresentation,
              pimId: accessory?.pimId,
              quantity: nodeData.quantity,
              orderCode: {
                state: {
                  optionStates: accessory?.optionStates,
                },
                seriesId: accessory?.seriesId,
                currentStrategyId: accessory?.currentStrategyId,
              },
              nodes: null,
            };
          }) ?? null)
        : null,
    },
    callback: (node) => {
      if (node.parentId === nodeData.parentId) {
        return {
          ...node,
          id: nodeData.id,
        };
      }
      return node;
    },
  });

  if (newNodeState.error) {
    throw new Error(newNodeState.error.message);
  }

  processNodes(
    [
      {
        action: "DELETE",
        nodes: [{ id: nodeData.id }],
      },
      {
        action: "CREATE",
        nodes: newNodeState.nodesToRequest ?? [],
      },
    ],
    project,
    language,
  );

  if (newNodeState.subTree) {
    Object.assign(nodeData, newNodeState.subTree);
  }

  callback?.(nodeData);

  return [...tree];
};
